import List from '@pelckmans/elementis/components/list';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { isScorableMedialink, isWeblinkWithNewWindowTarget, Medialink } from '../../../../api/medialink';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../constants/analyticsEvents';
import IdentityContext from '../../../../contexts/IdentityContext';
import ModalContext from '../../../../contexts/ModalContext';
import ModuleContext from '../../_contexts/ModuleContext';
import { MaterialIcon } from '../../components/MaterialIcon';
import SelectionContext from '../_contexts/SelectionContext';
import TableOfContentsContext from '../_contexts/TableOfContentsContext';
import useMedialinks from '../hooks/medialink/useMedialinks';
import MaterialPills from './MaterialPills';
import PinnedMaterialButton from './pinned-material/PinnedMaterialButton';
import ScorableProgress from './ScorableProgress';
import MaterialListHeader from './MaterialListHeader';

type MedialinkListProps = {
  medialinks: Medialink[];
  title: string;
};

export default function MedialinkList({ medialinks, title }: MedialinkListProps) {
  const { t } = useTranslation();
  const { openMedialink } = useMedialinks();

  const {
    identity: { isTeacherAlike },
  } = useContext(IdentityContext);
  const { module, selectedModule } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);
  const { showModal } = useContext(ModalContext);
  const { showSelection, selectedMedialinks, addMedialinks, removeMedialinks } = useContext(SelectionContext);

  const handleOpenMedialink = useCallback(
    async (medialink: Medialink) => {
      if (!selectedNode || !selectedModule || !module) return;

      const modal = await openMedialink(
        medialink,
        selectedNode,
        module,
        selectedModule,
        undefined,
        undefined,
        ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL,
      );

      if (modal) {
        showModal(modal);
      }
    },
    [selectedModule, selectedNode, showModal, openMedialink, module],
  );

  const renderListItem = (medialink: Medialink) => {
    // TODO no action context menu when showSelection is active
    const actionArea = (
      <>
        {isScorableMedialink(medialink) && (
          <ScorableProgress unachievable={medialink.unachievable} score={medialink.score} />
        )}
        <PinnedMaterialButton type="medialink" materialId={medialink.id} />
      </>
    );

    const pillGroupProps = isTeacherAlike
      ? {
          'aria-label': t('PAGES.TOC_LEVEL.MATERIAL_PILLS.A11Y_LABEL'),
          children: (
            <MaterialPills
              isAssignment={medialink.assignments.length > 0}
              isShared={medialink.shares.length > 0}
              isStudent={medialink.group === 'student'}
              isTeacher={medialink.group === 'teacher'}
            />
          ),
          role: 'group',
        }
      : undefined;

    if (showSelection) {
      const isSelected = selectedMedialinks.includes(medialink.id);
      return (
        <List.Item
          key={medialink.id}
          title={medialink.name}
          actionArea={actionArea}
          pillGroupProps={pillGroupProps}
          as="checkbox"
          checked={isSelected}
          onClick={() => (isSelected ? removeMedialinks([medialink.id]) : addMedialinks([medialink.id]))}
        />
      );
    }

    // TODO: need to check mini-site with target new window + assignments(as student)
    if (isWeblinkWithNewWindowTarget(medialink)) {
      return (
        <List.Item
          key={medialink.id}
          title={medialink.name}
          icon={<MaterialIcon materialType="medialink" contentType={medialink.contentType} />}
          actionArea={actionArea}
          pillGroupProps={pillGroupProps}
          as="link"
          href={medialink.externalMedia?.href}
          target="_blank"
        />
      );
    }

    return (
      <List.Item
        key={medialink.id}
        title={medialink.name}
        icon={<MaterialIcon materialType="medialink" contentType={medialink.contentType} />}
        actionArea={actionArea}
        pillGroupProps={pillGroupProps}
        as="button"
        onClick={() => {
          handleOpenMedialink(medialink);
        }}
      />
    );
  };

  return (
    <List>
      <MaterialListHeader
        title={title}
        showSelection={showSelection}
        selectedMaterial={selectedMedialinks}
        material={medialinks}
        addMaterial={addMedialinks}
        removeMaterial={removeMedialinks}
      />
      {medialinks.map(medialink => renderListItem(medialink))}
    </List>
  );
}
